import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseConfigService } from '@fuse/services/config';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { UserService } from 'app/core/user/user.service';
import { AuthenticationService } from '../../../../services/authentication.service';
import { Role, User } from 'app/models/user';
import { environment } from '../../../../../environments/environment';
import { FuseConfirmationConfig, FuseConfirmationService } from '@fuse/services/confirmation';
import { Company } from 'app/models/companies';
import { UsersService } from 'app/services/users.service';
import { UtilsService } from 'app/services/utils.service';
import { AppConfig, Scheme } from 'app/core/config/app.config';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ClassyLayoutComponent implements OnInit, OnDestroy
{
    isScreenSmall = false;
    navigation: Navigation = {
        compact: [],
        default: [],
        futuristic: [],
        horizontal: []
    };
    user: User = new User({
        role: new Role(),
        companies: [],
        active_company: new Company()
    });
    navigationIcon = 'heroicons_outline:chevron-right';
    scheme: Scheme = 'light';
    private _unsubscribeAll: Subject<void> = new Subject<void>();

    loading = true;

    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseConfigService: FuseConfigService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _authSrv: AuthenticationService,
        private _usersService: UsersService,
        private _utils: UtilsService,

    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    /**
     * Getter for app version
     */
    get appVersion(): string
    {
        const year = this.currentYear - 2023 + 1;
        const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
        return `${year}.${month}`;
    }

    get showLang(): boolean
    {
        return environment.showLang;
    }
    get showSearch(): boolean
    {
        return environment.showSearch;
    }
    get showShortcuts(): boolean
    {
        return environment.showShortcuts;
    }
    get showMessages(): boolean
    {
        return environment.showMessages;
    }
    get showLogout(): boolean
    {
        return environment.showLogout;
    }
    get showNotifications(): boolean
    {
        return environment.showNotifications;
    }
    get showProfile(): boolean
    {
        return environment.showProfile;
    }

    get isPro(): boolean
    {
        return environment.production;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe((takeUntil(this._unsubscribeAll)))
            .subscribe((user: User) => {
                this.user = user;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) => {

                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');

                // Set initial icon based on screen size
                this.navigationIcon = this.isScreenSmall ? 'heroicons_outline:chevron-right' : 'heroicons_outline:chevron-left';
            });

        this.loading = false;

        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: AppConfig) => {
                this.scheme = config.scheme === 'dark' ? 'dark' : 'light';
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();

            // Update the icon based on the new state
            this.navigationIcon = navigation.opened ? 'heroicons_outline:chevron-left' : 'heroicons_outline:chevron-right';
        }
    }

    toggleColorScheme(): void
    {
        const nextScheme: Scheme = this.scheme === 'dark' ? 'light' : 'dark';
        this._fuseConfigService.config = {scheme: nextScheme};
    }

    signOut(): void
    {

        const modal: FuseConfirmationConfig = {
            "title": "Cerrar sesión",
            "message": "Desea cerrar la sesión?",
            "icon": {
              "show": true,
              "name": "heroicons_outline:exclamation",
              "color": "warn"
            },
            "actions": {
              "confirm": {
                "show": true,
                "label": "Cerrar sesión",
                "color": "warn"
              },
              "cancel": {
                "show": true,
                "label": "Cancelar"
              }
            },
            "dismissible": true
          };

        const dialogRef = this._fuseConfirmationService.open( modal );

        dialogRef.afterClosed().subscribe((result) => {
            if(result == "confirmed"){
                this._router.navigate(['/sign-out']);
            }
        });

        //this._router.navigate(['/sign-out']);

    }

    changeCompany(): void
    {
        const activeCompanyId = this.user.active_company?.id;
        const nextCompany = this.user.companies?.find(company => company.id === activeCompanyId);

        if (!nextCompany?.id) {
            return;
        }

        this.user.active_company = new Company(nextCompany);
        this.user.company_active_id = nextCompany.id;
        this._authSrv.updateActiveCompany(this.user.active_company);
        this.setMainCompany(this.user.active_company);
    }

    setMainCompany(company: Company): void
    {

        this._usersService.setMain(company).subscribe({
            next: (result: User) => {

                const usr: User = new User(result);
                this._authSrv.updateUser(usr);

            },
            error: (err: any) => {

                const dialogRef = this._fuseConfirmationService.open(this._utils.alert_error("Problemas al marcar la empresa", "Error al marcar la empresa."));

                dialogRef.afterClosed().subscribe(() => {
                });

            }
        });
    }
}
